import React, { useEffect, useMemo, useState } from "react";
import { CalendarClock, ChartNoAxesColumnIncreasing, Clock3, PhoneCall, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./DashboardWidgets.css";

const getStartTime = (item) => {
  if (item?.startTime) return new Date(item.startTime);
  if (item?.dateTime) return new Date(item.dateTime);
  if (item?.date) return new Date(`${item.date}T${item.time || "00:00"}`);
  return null;
};

const getLocalDayKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const getPatientName = (patient) => {
  const nestedPatient = patient?.patient && typeof patient.patient === "object"
    ? patient.patient
    : null;
  const candidates = [
    patient?.patientName,
    patient?.patient,
    patient?.fullName,
    patient?.name,
    [patient?.firstName, patient?.lastName].filter(Boolean).join(" "),
    nestedPatient?.fullName,
    nestedPatient?.name,
    [nestedPatient?.firstName, nestedPatient?.lastName].filter(Boolean).join(" "),
  ];

  return candidates.find((candidate) => typeof candidate === "string" && candidate.trim())?.trim() || "";
};

const getRoomArrival = (room) => {
  const date = new Date(room?.arrivedAt || room?.arrivalTime || room?.createdAt || room?.updatedAt || "");
  return Number.isNaN(date.getTime()) ? null : date;
};

function formatWaitDuration(arrival, now, t) {
  if (!arrival) return t("dashboard.widgets.durationUnknown");
  const minutes = Math.max(0, Math.floor((now.getTime() - arrival.getTime()) / 60_000));
  if (minutes < 1) return t("dashboard.widgets.justArrived");
  return t("dashboard.widgets.waitingFor", { minutes });
}

function makeRevenueBuckets(consultations, period, now, locale) {
  const buckets = Array.from({ length: 7 }, (_, index) => {
    let start;
    let label;
    if (period === "day") {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (6 - index));
      label = start.toLocaleDateString(locale, { weekday: "short" });
    } else if (period === "week") {
      const currentMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const mondayOffset = (currentMonday.getDay() + 6) % 7;
      currentMonday.setDate(currentMonday.getDate() - mondayOffset - (6 - index) * 7);
      start = currentMonday;
      label = start.toLocaleDateString(locale, { day: "numeric", month: "short" });
    } else {
      start = new Date(now.getFullYear(), now.getMonth() - (6 - index), 1);
      label = start.toLocaleDateString(locale, { month: "short" });
    }

    const end = period === "day"
      ? new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1)
      : period === "week"
        ? new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7)
        : new Date(start.getFullYear(), start.getMonth() + 1, 1);

    return { start, end, label, value: 0 };
  });

  consultations.forEach((consultation) => {
    const date = new Date(consultation?.consultationDateTime || consultation?.createdAt || "");
    const amount = Number(consultation?.totalPrice ?? consultation?.consultationPrice ?? 0);
    if (Number.isNaN(date.getTime()) || !Number.isFinite(amount) || amount <= 0) return;
    const bucket = buckets.find(({ start, end }) => date >= start && date < end);
    if (bucket) bucket.value += amount;
  });

  return buckets.map((bucket) => ({
    ...bucket,
    title: `${bucket.label}: ${new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(bucket.value)} TND`,
  }));
}

export default function DashboardWidgets({
  appointments = [],
  waitingRooms = [],
  consultations = [],
  lastUpdatedAt,
  onCallPatient,
  onOpenPatient,
  onStartConsultation,
}) {
  const { t } = useTranslation("cabinetDashboard");
  const [now, setNow] = useState(() => new Date());
  const [revenuePeriod, setRevenuePeriod] = useState("day");
  const locale = t("dashboard.locale", { defaultValue: "fr-FR" });

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const todaysAppointments = useMemo(() => {
    const todayKey = getLocalDayKey(now);
    return appointments
      .filter((appointment) =>
        appointment?.source !== "event" &&
        !["cancelled", "canceled"].includes(String(appointment?.status || "").toLowerCase())
      )
      .map((appointment) => ({ appointment, start: getStartTime(appointment) }))
      .filter(({ start }) => start && !Number.isNaN(start.getTime()) && getLocalDayKey(start) === todayKey)
      .sort((a, b) => a.start - b.start);
  }, [appointments, now]);

  const nextPatient = todaysAppointments.find(({ start }) => start >= now);
  const nextStart = nextPatient?.start;
  const nextInMinutes = nextStart
    ? Math.max(0, Math.ceil((nextStart.getTime() - now.getTime()) / 60_000))
    : null;
  const revenueBuckets = useMemo(
    () => makeRevenueBuckets(consultations, revenuePeriod, now, locale),
    [consultations, revenuePeriod, now, locale]
  );
  const maxRevenue = Math.max(1, ...revenueBuckets.map(({ value }) => value));

  const weekdayCounts = new Map();
  appointments.forEach((appointment) => {
    if (
      appointment?.source === "event" ||
      ["cancelled", "canceled"].includes(String(appointment?.status || "").toLowerCase())
    ) return;
    const start = getStartTime(appointment);
    if (!start || Number.isNaN(start.getTime())) return;
    const weekday = start.getDay();
    weekdayCounts.set(weekday, (weekdayCounts.get(weekday) || 0) + 1);
  });
  const busiestDay = [...weekdayCounts.entries()].sort((a, b) => b[1] - a[1])[0];
  const busiestDayName = busiestDay
    ? new Date(2026, 0, 4 + busiestDay[0]).toLocaleDateString(locale, { weekday: "long" })
    : null;
  const unconfirmedCount = todaysAppointments.filter(({ appointment }) =>
    ["pending", "requested", "unconfirmed"].includes(String(appointment?.status || "").toLowerCase())
  ).length;

  const updateAge = lastUpdatedAt
    ? Math.max(0, Math.floor((now.getTime() - new Date(lastUpdatedAt).getTime()) / 1000))
    : null;
  const updateAgeLabel = updateAge == null
    ? "—"
    : updateAge < 60
      ? t("dashboard.widgets.secondsAgo", { seconds: updateAge })
      : t("dashboard.widgets.minutesAgo", { minutes: Math.floor(updateAge / 60) });

  return (
    <div className="dashboard-widgets">
      <section className="dw-card dw-card--next" aria-labelledby="dw-next-title">
        <div className="dw-card__heading">
          <CalendarClock aria-hidden="true" />
          <h2 id="dw-next-title">{t("dashboard.widgets.nextPatient")}</h2>
          {waitingRooms.length > 0 && (
            <span className="dw-live" aria-label={t("dashboard.widgets.waitingLive")}>
              <span className="dw-live__dot" aria-hidden="true" />
              LIVE
            </span>
          )}
        </div>
        {nextPatient ? (
          <>
            <p className="dw-next__name">{getPatientName(nextPatient.appointment) || t("dashboard.widgets.patient")}</p>
            <p className="dw-next__meta">
              <strong>{nextStart.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}</strong>
              <span>{nextInMinutes === 0 ? t("dashboard.widgets.startingNow") : t("dashboard.widgets.inMinutes", { minutes: nextInMinutes })}</span>
            </p>
            <p className="dw-next__reason">{nextPatient.appointment.notes || nextPatient.appointment.reason || t("dashboard.widgets.noReason")}</p>
            <div className="dw-next__actions">
              <button type="button" className="dw-button dw-button--primary" onClick={() => onStartConsultation?.(nextPatient.appointment)}>
                {t("dashboard.widgets.startConsultation")}
              </button>
              <button type="button" className="dw-button dw-button--secondary" onClick={() => onOpenPatient?.(nextPatient.appointment)}>
                {t("dashboard.widgets.viewRecord")}
              </button>
            </div>
          </>
        ) : (
          <div className="dw-empty">
            <CalendarClock aria-hidden="true" />
            <p>{t("dashboard.widgets.noNextPatient")}</p>
          </div>
        )}
      </section>

      <section className="dw-card" aria-labelledby="dw-waiting-title">
        <div className="dw-card__heading">
          <Clock3 aria-hidden="true" />
          <h2 id="dw-waiting-title">{t("dashboard.widgets.waitingRoom")}</h2>
          <span className="dw-card__count">{waitingRooms.length}</span>
        </div>
        {waitingRooms.length ? (
          <ul className="dw-waiting-list">
            {waitingRooms.map((room) => (
              <li className="dw-waiting-item" key={room.id || room.roomId}>
                <span className="dw-waiting-item__avatar"><UserRound aria-hidden="true" /></span>
                <span className="dw-waiting-item__details">
                  <strong>{getPatientName(room) || t("dashboard.widgets.patient")}</strong>
                  <span>{formatWaitDuration(getRoomArrival(room), now, t)}</span>
                </span>
                <button
                  type="button"
                  className="dw-call-button"
                  onClick={() => onCallPatient?.(room)}
                  aria-label={t("dashboard.widgets.callPatient", { name: getPatientName(room) || t("dashboard.widgets.patient") })}
                  title={t("dashboard.widgets.callPatient", { name: getPatientName(room) || t("dashboard.widgets.patient") })}
                >
                  <PhoneCall aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="dw-empty">
            <UserRound aria-hidden="true" />
            <p>{t("dashboard.widgets.emptyWaitingRoom")}</p>
          </div>
        )}
      </section>

      <section className="dw-card" aria-labelledby="dw-insights-title">
        <div className="dw-card__heading">
          <ChartNoAxesColumnIncreasing aria-hidden="true" />
          <h2 id="dw-insights-title">{t("dashboard.widgets.insights")}</h2>
        </div>
        <ul className="dw-insights">
          {busiestDayName && (
            <li>{t("dashboard.widgets.busiestDay", { day: busiestDayName })}</li>
          )}
          {unconfirmedCount > 0 && (
            <li>{t("dashboard.widgets.unconfirmed", { count: unconfirmedCount })}</li>
          )}
          {!busiestDayName && unconfirmedCount === 0 && (
            <li>{t("dashboard.widgets.noInsights")}</li>
          )}
        </ul>
      </section>

      <section className="dw-card" aria-labelledby="dw-revenue-title">
        <div className="dw-card__heading dw-revenue__heading">
          <ChartNoAxesColumnIncreasing aria-hidden="true" />
          <h2 id="dw-revenue-title">{t("dashboard.widgets.revenue")}</h2>
          <div className="dw-revenue__toggle" role="group" aria-label={t("dashboard.widgets.revenuePeriod")}>
            {["day", "week", "month"].map((period) => (
              <button
                type="button"
                key={period}
                className={revenuePeriod === period ? "is-active" : ""}
                aria-pressed={revenuePeriod === period}
                onClick={() => setRevenuePeriod(period)}
              >
                {t(`dashboard.widgets.periods.${period}`)}
              </button>
            ))}
          </div>
        </div>
        {consultations.length ? (
          <div className="dw-chart" role="img" aria-label={t("dashboard.widgets.revenueChartLabel")}>
            {revenueBuckets.map((bucket) => (
              <div className="dw-chart__bar-wrap" key={`${bucket.start.toISOString()}-${revenuePeriod}`} title={bucket.title}>
                <span className="dw-chart__value" aria-hidden="true">
                  {bucket.value > 0 ? new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(bucket.value) : ""}
                </span>
                <span
                  className="dw-chart__bar"
                  style={{ height: `${Math.max(bucket.value > 0 ? 8 : 2, (bucket.value / maxRevenue) * 100)}%` }}
                />
                <span className="dw-chart__label">{bucket.label}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="dw-empty dw-empty--compact">
            <ChartNoAxesColumnIncreasing aria-hidden="true" />
            <p>{t("dashboard.widgets.noRevenue")}</p>
          </div>
        )}
      </section>

      <p className="dw-updated" role="status">
        {t("dashboard.widgets.updated", { time: updateAgeLabel })}
      </p>
    </div>
  );
}
